import React, { useState } from "react";
import { sampleLogo } from "../../data/designAssets";
import { Highlight } from "../Highlight";
import { StepHeading } from "../StepHeading";
import { DesignCanvas } from "./DesignCanvas";
import { AddElementBar } from "./AddElementBar";
import { StudioPanel } from "./StudioPanel";
import { contrastInk } from "../../utils/color";
import type { Configurator } from "../../hooks/useConfigurator";
import type { DesignElement, MockupView, NewElement } from "../../types/configurator";
interface StudioStepProps {
  config: Configurator;
}
const ACCEPTED = ["image/png", "image/svg+xml", "image/jpeg"];
const MAX_BYTES = 20 * 1024 * 1024;
export function StudioStep({
  config
}: StudioStepProps) {
  const {
    mockup,
    color,
    design,
    marking,
    state
  } = config;
  const [activeZoneId, setActiveZoneId] = useState(mockup?.zones[0]?.id ?? "");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCliparts, setShowCliparts] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  if (!mockup || !color) return null;
  const activeZone = mockup.zones.find(z => z.id === activeZoneId) ?? mockup.zones[0];
  const view: MockupView = activeZone.view;
  const selected = design.elements.find(e => e.id === selectedId) ?? null;
  const ink = contrastInk(color.hex);
  function selectElement(el: DesignElement | null) {
    setSelectedId(el?.id ?? null);
    if (el) setActiveZoneId(el.zoneId);
  }
  function addAndSelect(el: NewElement) {
    const id = design.add(el);
    setSelectedId(id);
    if (state.provideLater) config.update("provideLater", false);
  }
  function addImage(src: string, name: string) {
    addAndSelect({
      kind: "image",
      src,
      name,
      zoneId: activeZone.id,
      x: 0.5,
      y: 0.5,
      size: 0.6,
      rotation: 0
    });
  }
  function handleFile(file: File) {
    if (!ACCEPTED.includes(file.type)) {
      setUploadError("Format non pris en charge : PNG, SVG ou JPEG uniquement.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setUploadError("Ce fichier dépasse 20 Mo.");
      return;
    }
    setUploadError(null);
    addImage(URL.createObjectURL(file), file.name);
  }
  function changeView(v: MockupView) {
    const first = mockup?.zones.find(z => z.view === v);
    if (first) setActiveZoneId(first.id);
    setSelectedId(null);
  }
  return <section data-id="element-342" data-mp="1ax18218gtmwk">
      <StepHeading title={<>
            Créez votre <Highlight tone="rose" data-id="element-344" data-mp="dcwv1fsypvr1">objet</Highlight>, en direct
          </>} subtitle="Ajoutez votre logo, un texte ou un pictogramme, déplacez-les sur le produit et voyez le rendu final selon la couleur et la technique choisies." data-id="element-343" data-mp="1awrey4s4izmn" />

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(320px,1fr)] xl:gap-10" data-id="element-345" data-mp="rzztbop408ff">
        <div className="border border-line lg:sticky lg:top-[68px]" data-id="element-346" data-mp="3qqrp601yaec6">
          <DesignCanvas mockup={mockup} hex={color.hex} markingId={marking?.id ?? null} design={design} view={view} activeZoneId={activeZone.id} selectedId={selectedId} onViewChange={changeView} onZoneClick={setActiveZoneId} onSelect={selectElement} onDropFile={handleFile} data-id="element-347" data-mp="3qof9jtjo0g2a" />
          <AddElementBar zoneName={activeZone.name} showCliparts={showCliparts} uploadError={uploadError} provideLater={state.provideLater} onFile={handleFile} onAddText={() => addAndSelect({
          kind: "text",
          text: "Votre texte",
          fontId: "helvetica",
          color: ink,
          zoneId: activeZone.id,
          x: 0.5,
          y: 0.5,
          size: 16,
          rotation: 0
        })} onToggleCliparts={() => setShowCliparts(s => !s)} onAddClipart={id => addAndSelect({
          kind: "clipart",
          clipartId: id,
          color: ink,
          zoneId: activeZone.id,
          x: 0.5,
          y: 0.5,
          size: 0.35,
          rotation: 0
        })} onSampleLogo={() => addImage(sampleLogo.src, sampleLogo.name)} onProvideLater={v => config.update("provideLater", v)} data-id="element-348" data-mp="1yupcsein5led" />
        </div>

        <StudioPanel config={config} activeZoneId={activeZone.id} selected={selected} onZoneChange={id => {
        setActiveZoneId(id);
        setSelectedId(null);
      }} onSelect={selectElement} onSelectId={setSelectedId} data-id="element-349" data-mp="3b6kxgjedm36f" />
      </div>
    </section>;
}